import React, { useState, useEffect } from 'react';
import { ChevronDown, Briefcase, Users, Calendar, MapPin } from 'lucide-react';

/**
 * JobSelector - Premium job selection component for pipeline filtering
 * Features: Glass morphism design, job metrics, elegant dropdown
 */
const JobSelector = ({ 
  selectedJob, 
  onJobSelect, 
  jobs = [], 
  jobMetrics = {}, 
  loading = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter jobs based on search term
  const filteredJobs = jobs?.filter(job => 
    job?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase() || '') ||
    job?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase() || '')
  ) || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event?.target?.closest('.job-selector-dropdown')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isOpen]);

  const handleJobSelect = (job) => {
    onJobSelect?.(job);
    setIsOpen(false);
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="relative">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl animate-pulse" />
              <div>
                <div className="w-32 h-4 bg-white/20 rounded animate-pulse mb-1" />
                <div className="w-20 h-3 bg-white/20 rounded animate-pulse" />
              </div>
            </div>
            <div className="w-6 h-6 bg-white/20 rounded animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative job-selector-dropdown">
      {/* Selected Job Display */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white/10 backdrop-blur-md border border-white/20 hover:border-white/30 rounded-2xl p-4 transition-all duration-200 hover:bg-white/15 group"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900 group-hover:text-violet-700 transition-colors">
                {selectedJob?.title || 'Select Job Position'}
              </h3>
              <p className="text-sm text-gray-600">
                {selectedJob?.department || 'Choose a position to view pipeline'}
              </p>
            </div>
          </div>
          
          {selectedJob && jobMetrics?.[selectedJob?.id] && (
            <div className="flex items-center gap-4 mr-2">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  {jobMetrics?.[selectedJob?.id]?.totalApplications || 0} candidates
                </div>
                <div className="text-xs text-gray-500">
                  Avg: {Math.round(jobMetrics?.[selectedJob?.id]?.averageTimeToHire || 0)}d to hire
                </div>
              </div>
            </div>
          )}

          <ChevronDown 
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`} 
          />
        </div>
      </button>
      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-xl border border-white/30 rounded-2xl shadow-2xl shadow-black/20 z-50 max-h-96 overflow-hidden">
          {/* Search Input */}
          <div className="p-4 border-b border-gray-100">
            <input
              type="text"
              placeholder="Search positions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full px-3 py-2 bg-gray-50/50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-300"
              autoFocus
            />
          </div>

          {/* Jobs List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredJobs?.length > 0 ? (
              filteredJobs?.map((job) => {
                const metrics = jobMetrics?.[job?.id];
                const isSelected = selectedJob?.id === job?.id;

                return (
                  <button
                    key={job?.id}
                    onClick={() => handleJobSelect(job)}
                    className={`w-full p-4 text-left hover:bg-violet-50/80 transition-colors border-b border-gray-50 last:border-b-0 ${
                      isSelected ? 'bg-violet-100/60 border-l-4 border-l-violet-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-1">
                          {job?.title}
                        </h4>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                          <div className="flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            <span>{job?.department || 'No Department'}</span>
                          </div>
                          
                          {job?.location && (
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              <span>{job?.location}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>
                              {new Date(job.created_at)?.toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </span>
                          </div>
                        </div>

                        {metrics && (
                          <div className="flex items-center gap-4 text-xs">
                            <div className="flex items-center gap-1 text-violet-600">
                              <Users className="w-3 h-3" />
                              <span className="font-medium">{metrics?.totalApplications}</span>
                              <span className="text-gray-500">applicants</span>
                            </div>
                            
                            {metrics?.averageTimeToHire > 0 && (
                              <div className="text-amber-600">
                                <span className="font-medium">{Math.round(metrics?.averageTimeToHire)}</span>
                                <span className="text-gray-500">d avg hire</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {isSelected && (
                        <div className="ml-3 mt-1">
                          <div className="w-2 h-2 bg-violet-500 rounded-full" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Briefcase className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">
                  {searchTerm ? 'No positions found' : 'No positions available'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobSelector;